import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ArrowDownRight,
  ArrowUpRight,
  CircleDollarSign,
  Handshake,
  History,
  ReceiptText,
  RotateCcw,
} from 'lucide-react';
import Layout from '@/components/Layout';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { useMyCourseRoyaltiesQuery } from '@/generated/graphql';
import { formatCents, formatExactCents } from '@/lib/format/currency';
import { cn } from '@/lib/utils';
import { WalletStatement } from '@/components/wallet/WalletStatement';
import { WithdrawableBalanceCard } from '@/components/wallet/WithdrawableBalanceCard';
import { formatStatementDateTime } from '@/lib/wallet/formatStatementDates';

const statusStyles: Record<string, string> = {
  pending: 'border-amber-500/30 bg-amber-500/10 text-amber-600 dark:text-amber-300',
  available:
    'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300',
  reversed: 'border-rose-500/30 bg-rose-500/10 text-rose-700 dark:text-rose-300',
};

const totalCards = [
  {
    key: 'accrued' as const,
    icon: CircleDollarSign,
    accent: 'text-foreground',
    surface: 'border-border/70',
  },
  {
    key: 'pending' as const,
    icon: History,
    accent: 'text-amber-600 dark:text-amber-300',
    surface: 'border-amber-500/20 bg-amber-500/[0.04]',
  },
  {
    key: 'available' as const,
    icon: ArrowUpRight,
    accent: 'text-emerald-700 dark:text-emerald-300',
    surface: 'border-emerald-500/20 bg-emerald-500/[0.04]',
  },
  {
    key: 'reversed' as const,
    icon: ArrowDownRight,
    accent: 'text-rose-700 dark:text-rose-300',
    surface: 'border-rose-500/20 bg-rose-500/[0.04]',
  },
];

const COURSE_ROYALTIES_PAGE_SIZE = 50;

const formatSaleDate = (value: string, locale: string, fallback: string) => {
  if (!value.trim()) return fallback;

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return fallback;

  try {
    return new Intl.DateTimeFormat(locale, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    }).format(date);
  } catch {
    return fallback;
  }
};

export const CourseRoyaltiesPage = () => {
  const { t, i18n } = useTranslation();
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadMoreFailed, setLoadMoreFailed] = useState(false);
  const {
    data, loading, error, refetch, fetchMore,
  } = useMyCourseRoyaltiesQuery({
    variables: { limit: COURSE_ROYALTIES_PAGE_SIZE, after: null },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });
  const royalties = data?.myCourseRoyalties;

  const dateLocale = i18n.language.toLowerCase().startsWith('en') ? 'en-US' : 'pt-BR';
  const nextCursor = royalties?.earnings_page_info.next_cursor;

  const handleLoadMore = async () => {
    if (loading || loadingMore || !royalties?.earnings_page_info.has_more || !nextCursor) return;
    setLoadingMore(true);
    setLoadMoreFailed(false);
    try {
      await fetchMore({
        variables: { limit: COURSE_ROYALTIES_PAGE_SIZE, after: nextCursor },
        updateQuery: (previous, { fetchMoreResult }) => {
          if (!fetchMoreResult) return previous;
          const knownIds = new Set(
            previous.myCourseRoyalties.earnings.map((earning) => earning.id),
          );
          const additionalEarnings = fetchMoreResult.myCourseRoyalties.earnings
            .filter((earning) => !knownIds.has(earning.id));
          return {
            ...fetchMoreResult,
            myCourseRoyalties: {
              ...fetchMoreResult.myCourseRoyalties,
              earnings: [
                ...previous.myCourseRoyalties.earnings,
                ...additionalEarnings,
              ],
            },
          };
        },
      });
    } catch {
      setLoadMoreFailed(true);
    } finally {
      setLoadingMore(false);
    }
  };

  if (loading && !royalties) {
    return (
      <Layout>
        <main
          className="flex-1 overflow-y-auto p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8"
          aria-busy="true"
        >
          <span className="sr-only">{t('courseRoyalties.loading')}</span>
          <div className="mx-auto max-w-6xl space-y-6">
            <Skeleton className="h-20 w-full rounded-xl" />
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {totalCards.map(({ key }) => (
                <Skeleton key={key} className="h-28 rounded-xl" />
              ))}
            </div>
            <Skeleton className="h-64 rounded-xl" />
          </div>
        </main>
      </Layout>
    );
  }

  if (error || !royalties) {
    return (
      <Layout>
        <main className="flex flex-1 items-center justify-center overflow-y-auto p-4 pb-24 sm:p-6 lg:p-8">
          <Card className="w-full max-w-lg border-destructive/25">
            <CardContent className="flex flex-col items-center px-6 py-12 text-center">
              <div className="mb-5 flex h-12 w-12 items-center justify-center rounded-full bg-destructive/10 text-destructive">
                <RotateCcw className="h-5 w-5" />
              </div>
              <h1 className="text-lg font-semibold">
                {t('courseRoyalties.error.title')}
              </h1>
              <p className="mt-2 max-w-sm text-sm text-muted-foreground">
                {t('courseRoyalties.error.description')}
              </p>
              <Button
                className="mt-6"
                onClick={() => void refetch({
                  limit: COURSE_ROYALTIES_PAGE_SIZE,
                  after: null,
                })}
              >
                {t('courseRoyalties.error.retry')}
              </Button>
            </CardContent>
          </Card>
        </main>
      </Layout>
    );
  }

  if (royalties.configurations.length === 0) {
    return (
      <Layout>
        <main className="flex flex-1 items-center justify-center overflow-y-auto p-4 pb-24 sm:p-6 lg:p-8">
          <Card className="w-full max-w-xl overflow-hidden border-border/70">
            <div className="h-1 bg-gradient-to-r from-transparent via-emerald-500/60 to-transparent" />
            <CardContent className="flex flex-col items-center px-6 py-14 text-center">
              <div className="mb-5 flex h-14 w-14 items-center justify-center rounded-2xl border border-border bg-muted/40">
                <Handshake className="h-6 w-6 text-muted-foreground" />
              </div>
              <h1 className="text-xl font-semibold">
                {t('courseRoyalties.empty.title')}
              </h1>
              <p className="mt-2 max-w-sm text-sm leading-6 text-muted-foreground">
                {t('courseRoyalties.empty.description')}
              </p>
            </CardContent>
          </Card>
        </main>
      </Layout>
    );
  }

  const totals = {
    accrued: royalties.accrued_cents,
    pending: royalties.pending_cents,
    available: royalties.available_cents,
    reversed: royalties.reversed_cents,
  };

  return (
    <Layout>
      <main className="flex-1 overflow-y-auto p-4 pb-24 sm:p-6 lg:p-8 lg:pb-8">
        <div className="mx-auto max-w-6xl space-y-6">
          <header className="flex items-start gap-4 border-b border-border/60 pb-6">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-emerald-500/25 bg-emerald-500/10">
              <ReceiptText className="h-5 w-5 text-emerald-600 dark:text-emerald-300" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">
                {t('courseRoyalties.title')}
              </h1>
              <p className="mt-1 text-sm text-muted-foreground">
                {t('courseRoyalties.subtitle')}
              </p>
            </div>
          </header>

          <section aria-labelledby="royalty-totals" className="space-y-3">
            <h2 id="royalty-totals" className="sr-only">
              {t('courseRoyalties.totals.title')}
            </h2>
            <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
              {totalCards.map(({ key, icon: Icon, accent, surface }) => (
                <Card key={key} className={cn('shadow-sm', surface)}>
                  <CardContent className="p-4 sm:p-5">
                    <div className="flex items-center justify-between gap-3">
                      <p className="text-xs font-medium uppercase tracking-[0.14em] text-muted-foreground">
                        {t(`courseRoyalties.totals.${key}`)}
                      </p>
                      <Icon className={cn('h-4 w-4 shrink-0', accent)} />
                    </div>
                    <p className={cn('mt-3 text-xl font-bold tabular-nums sm:text-2xl', accent)}>
                      {formatExactCents(totals[key], 'BRL')}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </section>

          <section className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <WithdrawableBalanceCard />
          </section>

          <section aria-labelledby="royalty-configurations">
            <Card className="overflow-hidden border-emerald-500/20 bg-gradient-to-r from-emerald-500/[0.06] to-transparent">
              <CardHeader className="pb-3">
                <CardTitle
                  id="royalty-configurations"
                  className="flex items-center gap-2 text-sm font-semibold uppercase tracking-[0.12em] text-muted-foreground"
                >
                  <Handshake className="h-4 w-4" />
                  {t('courseRoyalties.configurations.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="grid gap-3 pb-5 sm:grid-cols-2">
                {royalties.configurations.map((configuration) => (
                  <div
                    key={configuration.id}
                    className="flex items-center justify-between gap-4 rounded-xl border border-border/70 bg-background/80 px-4 py-3"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-sm font-semibold">
                        {configuration.course_title}
                      </p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {t('courseRoyalties.configurations.rate', {
                          rate: configuration.rate_percent,
                        })}
                      </p>
                    </div>
                    <Badge
                      variant="outline"
                      className={cn(
                        'shrink-0',
                        configuration.active
                          ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
                          : 'border-border text-muted-foreground',
                      )}
                    >
                      {t(
                        configuration.active
                          ? 'courseRoyalties.configurations.active'
                          : 'courseRoyalties.configurations.inactive',
                      )}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </section>

          <section aria-labelledby="royalty-earnings">
            <Card className="overflow-hidden border-border/70">
              <CardHeader className="border-b border-border/60 pb-4">
                <CardTitle id="royalty-earnings" className="text-base">
                  {t('courseRoyalties.earnings.title')}
                </CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {royalties.earnings.length === 0 ? (
                  <p className="px-5 py-10 text-center text-sm text-muted-foreground">
                    {t('courseRoyalties.earnings.empty')}
                  </p>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/30 hover:bg-muted/30">
                        <TableHead className="h-10 min-w-48">
                          {t('courseRoyalties.columns.course')}
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap">
                          {t('courseRoyalties.columns.date')}
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap">
                          {t('courseRoyalties.columns.releasesAt')}
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap text-right">
                          {t('courseRoyalties.columns.gross')}
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap text-right">
                          {t('courseRoyalties.columns.rate')}
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap text-right">
                          {t('courseRoyalties.columns.royalty')}
                        </TableHead>
                        <TableHead className="h-10 whitespace-nowrap text-right">
                          {t('courseRoyalties.columns.status')}
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {royalties.earnings.map((earning) => (
                        <TableRow key={earning.id}>
                          <TableCell className="py-3 font-medium">
                            {earning.course_title}
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-3 text-muted-foreground tabular-nums">
                            {formatSaleDate(
                              earning.sale_date,
                              dateLocale,
                              t('courseRoyalties.dateUnavailable'),
                            )}
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-3 text-muted-foreground tabular-nums">
                            {earning.releases_at
                              ? formatStatementDateTime(earning.releases_at, dateLocale)
                              : t('courseRoyalties.dateUnavailable')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-3 text-right text-muted-foreground tabular-nums">
                            {formatExactCents(earning.gross_amount_cents, 'BRL')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-3 text-right tabular-nums">
                            {earning.rate_percent}%
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-3 text-right font-semibold tabular-nums">
                            {formatCents(earning.royalty_cents, 'BRL')}
                          </TableCell>
                          <TableCell className="whitespace-nowrap py-3 text-right">
                            <Badge
                              variant="outline"
                              className={cn(
                                'justify-center',
                                statusStyles[earning.status] ?? 'text-muted-foreground',
                              )}
                            >
                              {t(`courseRoyalties.status.${earning.status}`)}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                )}
                {royalties.earnings_page_info.has_more && nextCursor ? (
                  <div className="flex flex-col items-center gap-2 border-t border-border/60 p-4">
                    {loadMoreFailed ? (
                      <p className="text-sm text-destructive" role="status">
                        {t('courseRoyalties.earnings.loadMoreError')}
                      </p>
                    ) : null}
                    <Button
                      type="button"
                      variant="outline"
                      disabled={loading || loadingMore}
                      onClick={() => void handleLoadMore()}
                    >
                      {loadingMore
                        ? t('courseRoyalties.earnings.loadingMore')
                        : t('courseRoyalties.earnings.loadMore')}
                    </Button>
                  </div>
                ) : null}
              </CardContent>
            </Card>
          </section>

          <WalletStatement title={t('walletStatement.walletTitle')} />
        </div>
      </main>
    </Layout>
  );
};

export default CourseRoyaltiesPage;
